import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { createBillingAddress } from "@store/actions/billingAddresses";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import instance from "@store/middleware/api";
import { clearCart, closeCart, setCheckoutStep, setPaymentInFlight } from "@store/reducers/cart";

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { ERoutes } from "@utils/constants";
import styled, { keyframes } from "styled-components";

import { ButtonLabel } from "@/components/ButtonGlyph";
import { getStripe } from "@/utils/stripe";

import {
  Body,
  BottomFade,
  DesktopHeader,
  FirstSectionLabel,
  FooterActions,
  PhoneHeader,
  ScrollArea,
  ScrollChevron,
  ScrollHint,
  useScrollAffordance,
} from "./phoneTemplate";
import {
  BackBtn,
  BtnSpinner,
  FormRow,
  IconCircle,
  InputField,
  InputGroup,
  PrimaryBtn,
  SectionLabel,
  SlidePanelFooter,
  StepSubtitle,
  StepTitle,
} from "./styles";
import { CA_PROVINCES, COUNTRIES, COUNTRY_CODES, US_STATES } from "./types";


const STATE_REQUIRED_COUNTRIES = new Set(["United States", "Canada"]);

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
`;

const ShakeWrap = styled.div<{ $shake: boolean }>`
  animation: ${({ $shake }) => ($shake ? shake : "none")} 0.5s ease;
`;

const DropdownWrap = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownTrigger = styled.div<{ $open: boolean; $error: boolean; $hasValue: boolean }>`
  width: 100%;
  padding: 14px 36px 14px 16px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid ${({ $error }) => ($error ? "#e47a6a" : "rgba(255, 255, 255, 0.2)")};
  border-radius: 8px;
  color: ${({ $hasValue }) => ($hasValue ? "#ffffff" : "rgba(255, 255, 255, 0.8)")};
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: border-color 0.2s;
  box-sizing: border-box;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%) ${({ $open }) => ($open ? "rotate(180deg)" : "rotate(0)")};
    width: 12px;
    height: 8px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23b9a089' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
    background-size: contain;
    background-repeat: no-repeat;
    transition: transform 0.2s;
  }
`;

const DropdownMenu = styled.div<{ $top: number; $left: number; $width: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  background: #EDE0D1;
  border-radius: 12px;
  z-index: 1400;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 320px;
`;

const CountrySearchSection = styled.div`
  padding: 8px;
  background: #EDE0D1;
  flex: 0 0 auto;
  position: relative;
  z-index: 2;
`;

const CountrySearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-radius: 8px;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  color: #fff;
  background: #9b7f67;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const DropdownList = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 4px 0 8px;
  min-height: 0;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #8B7460; border-radius: 3px; }
`;

const DropdownItem = styled.div<{ $selected: boolean }>`
  padding: 9px 16px;
  font-family: "Raleway", sans-serif;
  font-size: 0.9375rem;
  color: ${({ $selected }) => ($selected ? "#3a2616" : "#8B7460")};
  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: rgba(89, 65, 46, 0.08);
    color: #3a2616;
  }
`;

const CreditCardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#ebc28e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

type BillingFields = {
  firstName: string;
  lastName: string;
  email: string;
  address: string;
  addressLine2: string;
  city: string;
  // Conditional: required for US + Canada (Stripe AVS).
  stateRegion: string;
  zipCode: string;
  country: string;
  companyName: string;
  vatId: string;
};

const REQUIRED_FIELDS: (keyof BillingFields)[] = ["firstName", "lastName", "address", "city", "zipCode", "country"];

const PaymentForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { paymentIntentId, items: cartItems, tipAmount: cartTipAmount, tipType: cartTipType, termsAccepted, withdrawalWaiverAccepted } = useAppSelector((state) => state.cart);
  const cartTip = cartTipType === "skip" ? 0 : cartTipAmount;
  const user = useAppSelector((state) => state.user);
  const isAuthenticated = !!(user as any)?.user?.email;

  const [paying, setPaying] = useState(false);
  // True during the brief window after the card confirms while we poll for the
  // webhook-created order — keeps the button spinner up ("Finalizing…") so the
  // user never sees an intermediate page; we navigate straight to the order.
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Auto-scroll payment errors (rendered at the bottom of the scroll body)
  // into view so they're not missed below the fold.
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (error) errorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [error]);
  const [ready, setReady] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof BillingFields, boolean>>>({});
  // Per-field shake — only the inputs that failed validation wobble.
  const [shakeFields, setShakeFields] = useState<Partial<Record<keyof BillingFields, boolean>>>({});

  const [billing, setBilling] = useState<BillingFields>({
    firstName: (user as any)?.user?.firstName || "",
    lastName: "",
    email: (user as any)?.user?.email || "",
    address: "",
    addressLine2: "",
    city: "",
    stateRegion: "",
    zipCode: "",
    country: "",
    companyName: "",
    vatId: "",
  });

  // Business pairing: VAT + Company must both be filled or both empty.
  const hasVat = !!billing.vatId.trim();
  const hasCompany = !!billing.companyName.trim();
  const vatCompanyMismatch = hasVat !== hasCompany;

  const [billingLoaded, setBillingLoaded] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);
  const canScrollDown = useScrollAffordance(bodyRef);

  // Pre-fill from saved billing address
  React.useEffect(() => {
    if (!isAuthenticated || billingLoaded) return;
    setBillingLoaded(true);
    instance.get("/billing-addresses").then((resp) => {
      const addresses = resp.data || [];
      const defaultAddr = addresses.find((a: any) => a.isDefault) || addresses[0];
      if (defaultAddr) {
        setBilling((prev) => ({
          firstName: defaultAddr.firstName || prev.firstName,
          lastName: defaultAddr.lastName || prev.lastName,
          email: defaultAddr.email || prev.email || (user as any)?.user?.email || "",
          address: defaultAddr.street || prev.address,
          addressLine2: defaultAddr.addressLine2 || prev.addressLine2,
          city: defaultAddr.city || prev.city,
          stateRegion: defaultAddr.stateRegion || prev.stateRegion,
          zipCode: defaultAddr.zipCode || prev.zipCode,
          country: defaultAddr.country || prev.country,
          companyName: defaultAddr.companyName || prev.companyName,
          vatId: defaultAddr.vatId || prev.vatId,
        }));
      }
    }).catch(() => {});
  }, [isAuthenticated, billingLoaded]);

  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [countryHighlight, setCountryHighlight] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const countryInputRef = useRef<HTMLInputElement>(null);
  const [countryRect, setCountryRect] = useState({ top: 0, left: 0, width: 0 });

  const [stateOpen, setStateOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [stateHighlight, setStateHighlight] = useState(-1);
  const stateDropdownRef = useRef<HTMLDivElement>(null);
  const stateInputRef = useRef<HTMLInputElement>(null);
  const [stateRect, setStateRect] = useState({ top: 0, left: 0, width: 0 });

  // Reposition the portal-rendered dropdowns relative to their triggers.
  // Runs on open + on every scroll / resize while open so the dropdown
  // tracks its anchor as the page reflows.
  const repositionCountry = useCallback(() => {
    const r = dropdownRef.current?.getBoundingClientRect();
    if (r) setCountryRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);
  const repositionState = useCallback(() => {
    const r = stateDropdownRef.current?.getBoundingClientRect();
    if (r) setStateRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (countryOpen) repositionCountry();
  }, [countryOpen, repositionCountry]);
  useLayoutEffect(() => {
    if (stateOpen) repositionState();
  }, [stateOpen, repositionState]);

  useEffect(() => {
    if (!countryOpen && !stateOpen) return;
    const handler = () => { if (countryOpen) repositionCountry(); if (stateOpen) repositionState(); };
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [countryOpen, stateOpen, repositionCountry, repositionState]);

  // Close dropdowns on outside click. The dropdown menus are rendered
  // via createPortal at document.body, so we mark them with a data-attr
  // and skip the close when the click lands inside one.
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const insidePortal = target.closest('[data-cart-dropdown-portal="1"]');
      if (insidePortal) return;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setCountryOpen(false);
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(target)) {
        setStateOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleField = useCallback((field: keyof BillingFields, value: string) => {
    setBilling((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: false }));
    }
  }, [fieldErrors]);

  const handleBack = () => {
    dispatch(setCheckoutStep("review"));
  };

  const validate = (): boolean => {
    const errors: Partial<Record<keyof BillingFields, boolean>> = {};
    const required = isAuthenticated ? REQUIRED_FIELDS : [...REQUIRED_FIELDS, "email" as keyof BillingFields];

    for (const field of required) {
      if (!billing[field].trim()) {
        errors[field] = true;
      }
    }

    if (!isAuthenticated && billing.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billing.email)) {
      errors.email = true;
    }

    // State / province required for US + CA (Stripe AVS).
    if (STATE_REQUIRED_COUNTRIES.has(billing.country) && !billing.stateRegion.trim()) {
      errors.stateRegion = true;
    }

    if (vatCompanyMismatch) {
      if (!hasCompany) errors.companyName = true;
      if (!hasVat) errors.vatId = true;
    }

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Shake only the fields that failed validation.
      setShakeFields(errors);
      setTimeout(() => setShakeFields({}), 500);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    if (!validate()) return;

    setPaying(true);
    dispatch(setPaymentInFlight(true));
    setError(null);

    try {
      const billingAddress = {
        firstName: billing.firstName,
        lastName: billing.lastName,
        email: billing.email || (user as any)?.user?.email || "",
        street: billing.address,
        addressLine2: billing.addressLine2 || undefined,
        city: billing.city,
        stateRegion: billing.stateRegion || undefined,
        zipCode: billing.zipCode,
        country: billing.country,
        companyName: billing.companyName || undefined,
        vatId: billing.vatId || undefined,
        customerType: hasVat ? "business" : "private",
      };

      // Step 1: Stage the checkout against the PaymentIntent. NO order is
      // created yet — the order is materialized by the payment_intent.succeeded
      // webhook, so a declined/abandoned checkout never leaves an orphan order.
      if (paymentIntentId) {
        await instance.post("/stripe/checkout/prepare", {
          paymentIntentId,
          items: cartItems.map((item) => ({
            trackId: item.trackId,
            trackName: item.trackName,
            composerName: item.composerName,
            coverUrl: item.coverUrl,
            licenseType: item.licenseType,
            licenseLabel: item.licenseLabel,
            price: item.price,
            quantity: item.quantity,
          })),
          billingAddress,
          paymentMethod: "stripe",
          tip: cartTip,
          customerType: hasVat ? "business" : "private",
          vatId: billing.vatId || undefined,
          // What the user actually ticked on CheckoutView, not literals.
          // The CONTENT waiver ("not focused on music") was removed on
          // 2026-08-02 and is no longer collected or sent; the Music Use check
          // enforces that rule rather than the customer's word. Only the §18
          // FAGG withdrawal waiver remains, and it is a DIFFERENT consent from
          // the terms — see the store's comment on withdrawalWaiverAccepted.
          termsAccepted,
          withdrawalWaiverAccepted,
        });
      }

      // Step 2: Confirm payment with Stripe. The return_url is only used for
      // redirect-based methods (e.g. a 3DS challenge); for the common inline
      // card path Stripe resolves here and we handle success below. With no
      // order id yet, the redirect fallback carries the PaymentIntent so the
      // success page can resolve the order.
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${ERoutes.OrderSuccess}?pi=${paymentIntentId}`,
          payment_method_data: {
            billing_details: {
              name: `${billing.firstName} ${billing.lastName}`.trim(),
              email: billing.email || (user as any)?.user?.email || "",
              phone: "",
              address: {
                line1: billing.address,
                line2: "",
                city: billing.city,
                state: billing.stateRegion || "",
                postal_code: billing.zipCode,
                country: COUNTRY_CODES[billing.country] || "",
              },
            },
          },
        },
        redirect: "if_required",
      });

      if (result.error) {
        setError(result.error.message || "Payment failed. Please try again.");
        setPaying(false);
        dispatch(setPaymentInFlight(false));
      } else if (result.paymentIntent?.status === "succeeded") {
        // Card cleared. Keep the button spinner up ("Finalizing…") while the
        // webhook creates the order, then navigate straight to it — no
        // intermediate page.
        setFinalizing(true);
        const piId = result.paymentIntent.id;

        // Save billing address for next time (non-blocking).
        if (isAuthenticated) {
          try {
            await dispatch(createBillingAddress({
              firstName: billing.firstName,
              lastName: billing.lastName,
              street: billing.address,
              addressLine2: billing.addressLine2 || undefined,
              city: billing.city,
              stateRegion: billing.stateRegion || undefined,
              zipCode: billing.zipCode,
              country: billing.country,
              companyName: billing.companyName || undefined,
              vatId: billing.vatId || undefined,
              customerType: hasVat ? "business" : "private",
              isDefault: true,
            }) as any);
          } catch { /* non-blocking */ }
        }

        // Poll for the webhook-created order (usually 1–3s).
        const started = Date.now();
        let order: any = null;
        while (Date.now() - started < 30000) {
          try {
            const resp = await instance.get(`/orders/by-payment-intent/${encodeURIComponent(piId)}`);
            if (resp.data && resp.data.id) { order = resp.data; break; }
          } catch { /* not ready yet */ }
          await new Promise((r) => setTimeout(r, 1200));
        }

        dispatch(clearCart());
        dispatch(closeCart());
        // Navigate to the resolved order, or fall back to the PI-keyed success
        // page (which keeps polling) if the webhook is unusually slow.
        navigate(order?.id ? `${ERoutes.OrderSuccess}/${order.id}` : `${ERoutes.OrderSuccess}?pi=${piId}`);
      }
    } catch (_err: any) {
      setError("Something went wrong. Please try again.");
      setPaying(false);
      setFinalizing(false);
      dispatch(setPaymentInFlight(false));
    }
  };

  return (
    <>
      <PhoneHeader>
        <h2>Payment</h2>
      </PhoneHeader>

      <ScrollArea>
      <Body ref={bodyRef}>
      <DesktopHeader>
        <IconCircle>
          <CreditCardIcon />
        </IconCircle>
        <StepTitle>Payment</StepTitle>
        <StepSubtitle>Enter your billing and payment details</StepSubtitle>
      </DesktopHeader>

      <>
        <FirstSectionLabel><span>Billing Address</span></FirstSectionLabel>

        <FormRow>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.firstName}>
              <InputField type="text" value={billing.firstName} onChange={(e) => handleField("firstName", e.target.value)} placeholder="First name" $error={fieldErrors.firstName} />
            </ShakeWrap>
          </InputGroup>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.lastName}>
              <InputField type="text" value={billing.lastName} onChange={(e) => handleField("lastName", e.target.value)} placeholder="Last name" $error={fieldErrors.lastName} />
            </ShakeWrap>
          </InputGroup>
        </FormRow>

        {!isAuthenticated && (
          <InputGroup style={{ marginTop: "8px" }}>
            <ShakeWrap $shake={!!shakeFields.email}>
              <InputField type="email" value={billing.email} onChange={(e) => handleField("email", e.target.value)} placeholder="Email address" $error={fieldErrors.email} />
            </ShakeWrap>
          </InputGroup>
        )}

        <InputGroup style={{ marginTop: "8px" }}>
          <ShakeWrap $shake={!!shakeFields.address}>
            <InputField type="text" value={billing.address} onChange={(e) => handleField("address", e.target.value)} placeholder="Street address" $error={fieldErrors.address} />
          </ShakeWrap>
        </InputGroup>

        <InputGroup style={{ marginTop: "8px" }}>
          <InputField type="text" value={billing.addressLine2} onChange={(e) => handleField("addressLine2", e.target.value)} placeholder="Apartment, suite, etc. (optional)" />
        </InputGroup>

        <FormRow style={{ marginTop: "8px" }}>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.city}>
              <InputField type="text" value={billing.city} onChange={(e) => handleField("city", e.target.value)} placeholder="City" $error={fieldErrors.city} />
            </ShakeWrap>
          </InputGroup>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.zipCode}>
              <InputField type="text" value={billing.zipCode} onChange={(e) => handleField("zipCode", e.target.value)} placeholder="ZIP / Postal code" $error={fieldErrors.zipCode} />
            </ShakeWrap>
          </InputGroup>
        </FormRow>

        <InputGroup style={{ marginTop: "8px", marginBottom: "20px" }}>
          <ShakeWrap $shake={!!shakeFields.country}>
          <DropdownWrap ref={dropdownRef}>
            <DropdownTrigger
              $open={countryOpen}
              $error={!!fieldErrors.country}
              $hasValue={!!billing.country}
              onClick={() => { setCountryOpen(!countryOpen); setCountrySearch(""); setCountryHighlight(-1); setTimeout(() => countryInputRef.current?.focus(), 50); }}
            >
              {billing.country || "Select country"}
            </DropdownTrigger>
            {countryOpen && createPortal(
              <DropdownMenu
                data-cart-dropdown-portal="1"
                $top={countryRect.top}
                $left={countryRect.left}
                $width={countryRect.width}
              >
                <CountrySearchSection>
                  <CountrySearchInput
                    ref={countryInputRef}
                    type="text"
                    value={countrySearch}
                    onChange={(e) => { setCountrySearch(e.target.value); setCountryHighlight(0); }}
                    onKeyDown={(e) => {
                      const filtered = [...COUNTRIES].sort().filter((c) => c.toLowerCase().includes(countrySearch.toLowerCase()));
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setCountryHighlight((prev) => Math.min(prev + 1, filtered.length - 1));
                      } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setCountryHighlight((prev) => Math.max(prev - 1, 0));
                      } else if (e.key === "Enter" && countryHighlight >= 0 && countryHighlight < filtered.length) {
                        e.preventDefault();
                        const picked = filtered[countryHighlight];
                        handleField("country", picked);
                        setCountryOpen(false);
                        setCountrySearch("");
                        setCountryHighlight(-1);
                        if (!STATE_REQUIRED_COUNTRIES.has(picked)) {
                          setBilling((prev: BillingFields) => ({ ...prev, stateRegion: "" }));
                        }
                      } else if (e.key === "Escape") {
                        setCountryOpen(false);
                        setCountrySearch("");
                        setCountryHighlight(-1);
                      }
                    }}
                    placeholder="Search..."
                  />
                </CountrySearchSection>
                <DropdownList>
                  {[...COUNTRIES].sort().filter((c) => c.toLowerCase().includes(countrySearch.toLowerCase())).map((c, i) => (
                    <DropdownItem
                      key={c}
                      $selected={billing.country === c}
                      style={i === countryHighlight ? { background: "rgba(89, 65, 46, 0.12)", color: "#3a2616" } : undefined}
                      onClick={() => {
                        handleField("country", c);
                        setCountryOpen(false);
                        setCountrySearch("");
                        setCountryHighlight(-1);
                        // Drop any stale state value when leaving US/CA — a
                        // CA-province code in a US field would fail Stripe AVS.
                        if (!STATE_REQUIRED_COUNTRIES.has(c)) {
                          setBilling((prev: BillingFields) => ({ ...prev, stateRegion: "" }));
                        }
                      }}
                    >
                      {c}
                    </DropdownItem>
                  ))}
                </DropdownList>
              </DropdownMenu>,
              document.body,
            )}
          </DropdownWrap>
          </ShakeWrap>
        </InputGroup>

        {/* State / province for US + CA — Stripe AVS depends on it. */}
        {STATE_REQUIRED_COUNTRIES.has(billing.country) && (() => {
          const list = billing.country === "Canada" ? CA_PROVINCES : US_STATES;
          const filtered = list.filter((s) => s.name.toLowerCase().includes(stateSearch.toLowerCase()));
          const selected = list.find((s) => s.code === billing.stateRegion);
          const placeholder = billing.country === "Canada" ? "Province" : "State";
          return (
            <InputGroup>
              <ShakeWrap $shake={!!shakeFields.stateRegion}>
              <DropdownWrap ref={stateDropdownRef}>
                <DropdownTrigger
                  $open={stateOpen}
                  $error={!!fieldErrors.stateRegion}
                  $hasValue={!!billing.stateRegion}
                  onClick={() => {
                    setStateOpen(!stateOpen);
                    setStateSearch("");
                    setStateHighlight(-1);
                    setTimeout(() => stateInputRef.current?.focus(), 50);
                  }}
                >
                  {selected?.name || placeholder}
                </DropdownTrigger>
                {stateOpen && createPortal(
                  <DropdownMenu
                    data-cart-dropdown-portal="1"
                    $top={stateRect.top}
                    $left={stateRect.left}
                    $width={stateRect.width}
                  >
                    <CountrySearchSection>
                      <CountrySearchInput
                        ref={stateInputRef}
                        type="text"
                        value={stateSearch}
                        onChange={(e) => { setStateSearch(e.target.value); setStateHighlight(0); }}
                        onKeyDown={(e) => {
                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setStateHighlight((prev) => Math.min(prev + 1, filtered.length - 1));
                          } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setStateHighlight((prev) => Math.max(prev - 1, 0));
                          } else if (e.key === "Enter" && stateHighlight >= 0 && stateHighlight < filtered.length) {
                            e.preventDefault();
                            handleField("stateRegion", filtered[stateHighlight].code);
                            setStateOpen(false);
                            setStateSearch("");
                            setStateHighlight(-1);
                          } else if (e.key === "Escape") {
                            setStateOpen(false);
                            setStateSearch("");
                            setStateHighlight(-1);
                          }
                        }}
                        placeholder="Search..."
                      />
                    </CountrySearchSection>
                    <DropdownList>
                      {filtered.map((s, i) => (
                        <DropdownItem
                          key={s.code}
                          $selected={billing.stateRegion === s.code}
                          style={i === stateHighlight ? { background: "rgba(89, 65, 46, 0.12)", color: "#3a2616" } : undefined}
                          onClick={() => {
                            handleField("stateRegion", s.code);
                            setStateOpen(false);
                            setStateSearch("");
                            setStateHighlight(-1);
                          }}
                        >
                          {s.name}
                        </DropdownItem>
                      ))}
                    </DropdownList>
                  </DropdownMenu>,
                  document.body,
                )}
              </DropdownWrap>
              </ShakeWrap>
            </InputGroup>
          );
        })()}

        {/* Business pair — VAT + Company must be both filled or both empty.
            §14a UStG: B2B invoices need both, B2C neither. Per-field shake
            now lives directly on each input so only the offender(s) wobble
            on a mismatched submission. */}
        <FormRow style={{ marginTop: "8px" }}>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.companyName}>
              <InputField
                type="text"
                value={billing.companyName}
                onChange={(e) => handleField("companyName", e.target.value)}
                placeholder="Company (optional)"
                $error={!!fieldErrors.companyName || (vatCompanyMismatch && !hasCompany)}
              />
            </ShakeWrap>
          </InputGroup>
          <InputGroup>
            <ShakeWrap $shake={!!shakeFields.vatId}>
              <InputField
                type="text"
                value={billing.vatId}
                onChange={(e) => handleField("vatId", e.target.value)}
                placeholder="VAT (optional)"
                $error={!!fieldErrors.vatId || (vatCompanyMismatch && !hasVat)}
              />
            </ShakeWrap>
          </InputGroup>
        </FormRow>
      </>

      <SectionLabel><span>Payment Method</span></SectionLabel>
      <div style={{ marginBottom: "20px" }}>
        <PaymentElement
          onReady={() => setReady(true)}
          onChange={(event) => setPaymentComplete(event.complete)}
          options={{
            layout: "tabs",
            business: { name: "Audio Sanctum" },
            fields: {
              billingDetails: "never",
            },
            wallets: {
              applePay: "auto",
              googlePay: "auto",
              link: "auto",
            },
          }}
        />
      </div>

      {error && (
        <div ref={errorRef} style={{
          padding: "12px 14px",
          borderRadius: "8px",
          background: "#d23b2c",
          border: "1px solid #d23b2c",
          color: "#ffffff",
          fontSize: "0.875rem",
          fontWeight: 500,
          fontFamily: "'Raleway', sans-serif",
          marginBottom: "16px",
        }}>
          {error}
        </div>
      )}
      </Body>
      <BottomFade $visible={canScrollDown} aria-hidden="true"><ScrollHint><span>Scroll</span><ScrollChevron /></ScrollHint></BottomFade>
      </ScrollArea>

      <SlidePanelFooter>
        <FooterActions>
          <BackBtn onClick={handleBack} disabled={paying || finalizing}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><polyline points="12 19 5 12 12 5" />
            </svg>
            {" "}Back
          </BackBtn>
          <PrimaryBtn
            onClick={handleSubmit}
            disabled={paying || finalizing || !ready || !stripe || !paymentComplete}
            $dimmed={!paymentComplete || vatCompanyMismatch}
            $busy={paying || finalizing}
            style={vatCompanyMismatch ? { cursor: "not-allowed" } : undefined}
          >
            {(paying || finalizing) && <BtnSpinner />}
            {finalizing ? "Finalizing..." : paying ? "Processing..." : <ButtonLabel glyph="dollar" iconFirst muted={paying || finalizing || !ready || !stripe || !paymentComplete}>Pay Now</ButtonLabel>}
          </PrimaryBtn>
        </FooterActions>
      </SlidePanelFooter>
    </>
  );
};

const PaymentView: React.FC = () => {
  const { paymentClientSecret } = useAppSelector((state) => state.cart);

  if (!paymentClientSecret) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "rgba(251, 244, 236, 0.5)", fontFamily: "'Raleway', sans-serif" }}>
        Loading payment...
      </div>
    );
  }

  return (
    <Elements
      // Key on the clientSecret: Stripe's <Elements> binds the PaymentElement to
      // the clientSecret at MOUNT and ignores later changes to the option. Each
      // "Continue to payment" mints a fresh PaymentIntent, so without remounting,
      // confirmPayment would charge the STALE intent while checkout/prepare staged
      // the new one — the charge then has no matching pending_checkout and the
      // webhook skips it (paid, but no order). The key forces a clean remount.
      key={paymentClientSecret}
      stripe={getStripe()}
      options={{
        clientSecret: paymentClientSecret,
        appearance: {
          theme: "flat",
          variables: {
            colorPrimary: "#ebc28e",
            colorBackground: "#513a28",
            colorText: "#ffffff",
            colorTextSecondary: "rgba(251, 244, 236, 0.5)",
            colorTextPlaceholder: "rgba(255, 255, 255, 0.5)",
            colorDanger: "#e47a6a",
            colorIcon: "#ebc28e",
            colorIconHover: "#f7ede6",
            fontFamily: "'Raleway', sans-serif",
            fontSizeBase: "0.9375rem",
            borderRadius: "8px",
            spacingUnit: "4px",
            focusBoxShadow: "none",
            focusOutline: "none",
          },
          rules: {
            ".Input": {
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              color: "#ffffff",
              // 16px (not the 15px base): iOS Safari auto-zooms when a focused
              // input's font is < 16px. Keep card fields at 16px to stop the zoom.
              fontSize: "16px",
              padding: "14px 16px",
              boxShadow: "none",
              transition: "border-color 0.2s ease",
            },
            ".Input:focus": {
              border: "1px solid rgba(235, 194, 142, 0.5)",
              boxShadow: "none",
              outline: "none",
            },
            ".Input::placeholder": {
              color: "rgba(255, 255, 255, 0.8)",
            },
            ".Input--invalid": {
              border: "1px solid #e47a6a",
              boxShadow: "none",
            },
            ".Label": {
              color: "rgba(251, 244, 236, 0.7)",
              fontSize: "0.8125rem",
              fontWeight: "500",
              marginBottom: "6px",
            },
            ".Tab": {
              backgroundColor: "rgba(255, 255, 255, 0.06)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              color: "rgba(251, 244, 236, 0.6)",
              boxShadow: "none",
              transition: "all 0.2s ease",
            },
            ".Tab:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              border: "1px solid rgba(235, 194, 142, 0.3)",
              color: "#fbf4ec",
            },
            ".Tab--selected": {
              backgroundColor: "rgba(235, 194, 142, 0.1)",
              border: "1px solid rgba(235, 194, 142, 0.4)",
              color: "#ebc28e",
              boxShadow: "none",
            },
            ".Tab--selected:hover": {
              backgroundColor: "rgba(235, 194, 142, 0.12)",
              color: "#ebc28e",
            },
            ".TabIcon": {
              fill: "rgba(251, 244, 236, 0.5)",
            },
            ".TabIcon--selected": {
              fill: "#ebc28e",
            },
            ".TabLabel": {
              color: "inherit",
            },
            ".Block": {
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
            },
            ".DropdownItem": {
              backgroundColor: "rgba(81, 58, 40, 0.95)",
              color: "#fbf4ec",
            },
            ".DropdownItem--highlight": {
              backgroundColor: "rgba(235, 194, 142, 0.15)",
              color: "#ebc28e",
            },
            ".Action": {
              color: "#ebc28e",
            },
            ".Action:hover": {
              color: "#f7ede6",
            },
            ".Error": {
              color: "#e47a6a",
            },
            ".CheckboxInput": {
              backgroundColor: "rgba(255, 255, 255, 0.12)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "4px",
            },
            ".CheckboxInput--checked": {
              backgroundColor: "#ebc28e",
              borderColor: "#ebc28e",
            },
          },
        },
      }}
    >
      <PaymentForm />
    </Elements>
  );
};

export default PaymentView;
